import React, { useState, useEffect } from "react";
import { login, register as registerUser, forgotPassword, resetPassword, api } from "../api";

export default function AuthComponent({ register, onAuth, onNavigate }) {
  const [usern, setUsern]   = useState("");
  const [email, setEmail]   = useState("");
  const [pwd, setPwd]       = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [msg, setMsg]       = useState("");
  const [msgType, setMsgType] = useState("error");
  const [loading, setLoading] = useState(false);
  const [mode, setMode]     = useState("default"); // "default" | "forgot" | "reset"
  const [resetToken, setResetToken] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("reset_token");
    if (t) {
      setResetToken(t);
      setMode("reset");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setMsg("");
    setLoading(true);
    try {
      if (mode === "forgot") {
        await forgotPassword(usern || email);
        setMsg("If that account exists, a reset email has been sent. Check your inbox.");
        setMsgType("success");
        setLoading(false);
        return;
      }
      if (mode === "reset") {
        await resetPassword(resetToken, newPwd);
        setMsg("Password reset! You can now log in.");
        setMsgType("success");
        setLoading(false);
        setTimeout(() => setMode("default"), 2000);
        return;
      }
      if (register) {
        await registerUser(usern, pwd, email);
        setMsg("Account created! Logging you in...");
        setMsgType("success");
        const loginRes = await login(usern, pwd);
        setTimeout(() => {
          onAuth({ access_token: loginRes.data.access_token, username: loginRes.data.username });
        }, 800);
      } else {
        const res = await login(usern, pwd);
        setMsg("Welcome back!");
        setMsgType("success");
        setTimeout(() => {
          onAuth({ access_token: res.data.access_token, username: res.data.username });
        }, 400);
      }
    } catch (err) {
      setMsg(err.response?.data?.error || err.message || "Failed");
      setMsgType("error");
      setLoading(false);
    }
  };

  const inputCls = "w-full p-3 bg-gray-800/80 border border-white/10 rounded-lg focus:border-cyan-500/50 focus:outline-none transition-colors text-white placeholder-gray-500";

  if (mode === "reset") {
    return (
      <div className="max-w-md mx-auto p-6 bg-gray-900/80 backdrop-blur-sm rounded-xl border border-white/10 text-white space-y-4">
        <h2 className="text-2xl font-bold">Set New Password</h2>
        <p className="text-gray-400 text-sm">Enter your new password below.</p>
        <form onSubmit={submit} className="space-y-3">
          <input type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)}
            placeholder="New password (min 8 characters)" className={inputCls} disabled={loading} />
          <button className="btn-dynamic w-full py-3 font-semibold disabled:opacity-50" style={{"--btn-glow":"#00ff6a"}} disabled={loading}>
            {loading ? "Resetting..." : "Reset Password"}
          </button>
        </form>
        {msg && <StatusMsg msg={msg} type={msgType} />}
      </div>
    );
  }

  if (mode === "forgot") {
    return (
      <div className="max-w-md mx-auto p-6 bg-gray-900/80 backdrop-blur-sm rounded-xl border border-white/10 text-white space-y-4">
        <h2 className="text-2xl font-bold">Forgot Password</h2>
        <p className="text-gray-400 text-sm">Enter your username or email and we'll send a reset link.</p>
        <form onSubmit={submit} className="space-y-3">
          <input type="text" value={usern} onChange={e => setUsern(e.target.value)}
            placeholder="Username or email" className={inputCls} disabled={loading} />
          <button className="btn-dynamic w-full py-3 font-semibold disabled:opacity-50" style={{"--btn-glow":"#00ff6a"}} disabled={loading}>
            {loading ? "Sending..." : "Send Reset Link"}
          </button>
        </form>
        {msg && <StatusMsg msg={msg} type={msgType} />}
        <button onClick={() => { setMode("default"); setMsg(""); }}
          className="text-sm text-gray-400 hover:text-cyan-400 transition-colors w-full text-center">
          ← Back to login
        </button>
      </div>
    );
  }

  const handleGoogleSignIn = async () => {
    setMsg("");
    setLoading(true);
    try {
      // Check if Google OAuth is configured
      const statusRes = await api.get("/auth/google/status");
      if (!statusRes.data.enabled) {
        setMsg("Google Sign-In is not configured yet.");
        setMsgType("error");
        setLoading(false);
        return;
      }
      // Get the auth URL and open in popup
      const urlRes = await api.get("/auth/google/url");
      const authUrl = urlRes.data.auth_url;
      const width = 500, height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(authUrl, "google_oauth",
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`);

      // Listen for OAuth callback message
      const handler = async (event) => {
        if (event.data?.type === "google_oauth_success") {
          window.removeEventListener("message", handler);
          const { access_token, username } = event.data;
          onAuth({ access_token, username });
        }
      };
      window.addEventListener("message", handler);

      // Also poll for popup close
      const pollTimer = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(pollTimer);
          setLoading(false);
        }
      }, 500);
    } catch (err) {
      setMsg("Google Sign-In unavailable. Use email login.");
      setMsgType("error");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto p-6 bg-gray-900/80 backdrop-blur-sm rounded-xl border border-white/10 text-white space-y-4">
      <h2 className="text-2xl font-bold">{register ? "Create Account" : "Welcome Back"}</h2>
      <p className="text-gray-400 text-sm">
        {register ? "Sign up to start managing your social media with AI" : "Log in to your Kronos account"}
      </p>

      {/* Google Sign-In Button */}
      <button
        onClick={handleGoogleSignIn}
        disabled={loading}
        className="btn-neon-border w-full flex items-center justify-center gap-3 py-3 px-4 rounded-lg bg-white hover:bg-gray-100 transition-all duration-200 disabled:opacity-50"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
        <span className="text-gray-800 font-semibold text-sm">
          {register ? "Sign up with Google" : "Sign in with Google"}
        </span>
      </button>

      {/* Divider */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-xs text-gray-500 uppercase tracking-wider">or</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      <form onSubmit={submit} className="space-y-3">
        <input type="text" value={usern} onChange={e => setUsern(e.target.value)}
          placeholder={register ? "Username" : "Username or Email"} className={inputCls} disabled={loading} />
        {register && (
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="Email address (for notifications & password reset)"
            className={inputCls} disabled={loading} />
        )}
        <input type="password" value={pwd} onChange={e => setPwd(e.target.value)}
          placeholder="Password" className={inputCls} disabled={loading} />
        <button className="btn-neon-border w-full py-3 rounded-lg font-semibold bg-gray-800/80 text-white disabled:opacity-50 hover:bg-gray-700/80 transition-all duration-200"
          disabled={loading}>
          {loading ? "Please wait..." : register ? "Create Account" : "Login"}
        </button>
      </form>
      {!register && (
        <button onClick={() => { setMode("forgot"); setMsg(""); }}
          className="text-sm text-gray-400 hover:text-cyan-400 transition-colors w-full text-center">
          Forgot password?
        </button>
      )}
      {msg && <StatusMsg msg={msg} type={msgType} />}
    </div>
  );
}

function StatusMsg({ msg, type }) {
  return (
    <div className={`p-3 rounded-lg text-sm font-medium ${
      type === "success"
        ? "bg-green-500/10 border border-green-500/30 text-green-400"
        : "bg-red-500/10 border border-red-500/30 text-red-400"
    }`}>
      {msg}
    </div>
  );
}
